import React, { useState, useEffect, useRef } from "react";
import { faMagnifyingGlass, faUserLargeSlash } from "@fortawesome/free-solid-svg-icons";
import { NavLink, useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { Alert, Snackbar, Tooltip, IconButton } from "@mui/material";
import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CircleSlash,
  Clock3,
  Copy,
  Gauge,
  Mail,
  Medal,
  PauseCircle,
} from "lucide-react";

import Deletepopup from "../../pages/admin/Aresure";
import {
  alluserlist,
  generatefile,
  playeractive,
  updateClubFromFLTT,
  uploadExcelOfUser,
  userdelete,
} from "../funtionsBD/Mainservice";
import Uploadfile from "../../pages/admin/Uploadfile";
import Pagination from "../Pagination/pagination";
import { usePermissions } from "../../context/Permissioncontext";
import InvitePlayerDialog from "./InvitePlayerDialog";
import FlttMatchProposalDialog, {
  useFlttMatchReview,
} from "./FlttMatchProposalDialog";
import { AddNewcomerDialog, NewcomerDossierDialog } from "../newcomer/AddNewcomerDialog";
import { FlttFormDialog } from "../newcomer/FlttFormDialog";
import { isLuxembourgClubCountry, isLuxembourgFederationClubId } from "../../constants/countries";
import { formatRoleLabels, roleBadgeColor } from "../../utils/roleLabels";
import { isSuperAdminUser } from "../../utils/brandAuth";
import {
  openSwitchClubDialog,
  readStoredActiveClubId,
} from "../../utils/switchActiveClubClient";
import { canUpdateClubFromFltt } from "../../constants/countries";
import {
  formatPointsWeekDelta,
  formatRankingPlace,
} from "../../utils/flttRankingDisplay";
import "./UserManagement.css";

function resolveAccountStatusClient(item) {
  if (item.account_status) return item.account_status;
  if (item.isDeleted) return "deleted";
  if (item.isUnregistered || item.awaitingRegistration || !item.user_id) {
    return "awaiting_registration";
  }
  const registered =
    item.emailVerification === true || item.isProfileCompleted === true;
  if (registered) return "active";
  const email = String(item.user_email || "").trim();
  return email.includes("@") ? "invited" : "awaiting_registration";
}

function getInitials(user) {
  if (user.user_first_name && user.user_last_name) {
    return `${user.user_first_name.charAt(0)}${user.user_last_name.charAt(0)}`.toUpperCase();
  }
  if (user.user_first_name) return user.user_first_name.charAt(0).toUpperCase();
  if (user.user_last_name) return user.user_last_name.charAt(0).toUpperCase();
  if (user.user_alias) {
    const aliasParts = user.user_alias.trim().split(" ");
    if (aliasParts.length >= 2) {
      return `${aliasParts[0].charAt(0)}${aliasParts[aliasParts.length - 1].charAt(0)}`.toUpperCase();
    }
    return user.user_alias.charAt(0).toUpperCase();
  }
  if (user.first_name && user.last_name) {
    return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
  }
  if (user.full_name) {
    const nameParts = user.full_name.trim().split(" ");
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
    }
    return user.full_name.charAt(0).toUpperCase();
  }
  return "U";
}

function displayNameOf(item) {
  // Training guests are nickname-only until FLTT/admin validation.
  if (item.is_training_guest === true && item.user_alias) {
    return item.user_alias;
  }
  if (item.user_first_name && item.user_last_name) {
    return `${item.user_first_name} ${item.user_last_name}`;
  }
  return (
    item.user_alias ||
    item.user_first_name ||
    item.user_last_name ||
    item.user_email ||
    "-"
  );
}

function GuestVisitBadge({ item, t }) {
  if (!item?.is_training_guest) return null;
  const visits = Number(item.guest_visit_count) || 0;
  const threshold = Number(item.guest_license_threshold) || 3;
  const needsLicense = item.guest_needs_license === true || visits >= threshold;
  const pending =
    item.guest_link_status === "pending_match" &&
    Array.isArray(item.guest_match_suggestions) &&
    item.guest_match_suggestions.length > 0;
  return (
    <div className="um-guest-meta">
      <span
        className={`um-chip um-chip--guest-visits${needsLicense ? " um-chip--guest-license" : ""}`}
        title={
          needsLicense
            ? t("guestLicenseRequired", {
                defaultValue: "Ask this guest to create a FLTT license",
              })
            : t("guestVisitCount", {
                defaultValue: "Training visits as guest",
              })
        }
      >
        {t("guestVisitsShort", { defaultValue: "Guest" })} · {visits}/{threshold}
      </span>
      {pending ? (
        <span className="um-chip um-chip--guest-pending">
          {t("guestPendingMatch", { defaultValue: "Validate match" })}
        </span>
      ) : null}
    </div>
  );
}

function classementTone(value) {
  const v = String(value || "").trim().toUpperCase();
  if (!v) return "other";
  if (v.startsWith("A")) return "a";
  if (v.startsWith("B")) return "b";
  if (v.startsWith("C")) return "c";
  if (v.startsWith("D")) return "d";
  return "other";
}

function formatInviteDate(raw) {
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

function formatInviteDateShort(raw) {
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
    });
  } catch {
    return null;
  }
}

function DesktopEmailCell({ email, viaParent, t, onCopied }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const rootRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const popW = Math.min(352, window.innerWidth * 0.7);
      let left = r.left + r.width / 2 - popW / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - popW - 8));
      setCoords({ top: r.bottom + 6, left });
    };
    place();
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!email) {
    return <span className="um-muted">—</span>;
  }

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      onCopied?.(email);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="um-email-cell" ref={rootRef}>
      <button
        ref={btnRef}
        type="button"
        className={`um-email-ico${open ? " is-open" : ""}${viaParent ? " is-via" : ""}`}
        aria-expanded={open}
        aria-label={t("mail") || "Email"}
        title={viaParent ? t("viaParent") || "via parent" : email}
        onClick={() => setOpen((v) => !v)}
      >
        <Mail size={16} strokeWidth={2.2} aria-hidden />
      </button>
      {open ? (
        <div
          className="um-email-pop"
          role="dialog"
          aria-label={t("mail") || "Email"}
          style={{ top: coords.top, left: coords.left }}
        >
          <button
            type="button"
            className="um-email-pop__addr"
            onClick={copyEmail}
            title={t("copyEmail") || "Copy email"}
          >
            {email}
          </button>
          {viaParent ? (
            <span className="um-email__via">{t("viaParent") || "via parent"}</span>
          ) : null}
          <button
            type="button"
            className="um-email-pop__copy"
            onClick={copyEmail}
            aria-label={t("copyEmail") || "Copy email"}
          >
            {copied ? (
              <CheckCircle2 size={15} strokeWidth={2.2} aria-hidden />
            ) : (
              <Copy size={15} strokeWidth={2.2} aria-hidden />
            )}
            <span>{copied ? t("copied") || "Copied" : t("copy") || "Copy"}</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

function StatusChip({ status, t }) {
  if (status === "deleted") {
    return <span className="um-chip um-chip--deleted">{t("userStatusDeleted") || "Deleted"}</span>;
  }
  if (status === "awaiting_registration") {
    return (
      <span className="um-chip um-chip--awaiting">
        {t("userStatusUnregistered") || t("awaitingRegistration") || "Unregistered"}
      </span>
    );
  }
  if (status === "invited") {
    return <span className="um-chip um-chip--invited">{t("userStatusInvited") || "Invited"}</span>;
  }
  if (status === "inactive") {
    return <span className="um-chip um-chip--inactive">{t("inactive") || "Inactive"}</span>;
  }
  return (
    <span className="um-chip um-chip--active">
      {t("active") || "Active"}
    </span>
  );
}

function StatusIcon({ status, t }) {
  if (status === "deleted") {
    return (
      <span
        className="um-status-ico um-status-ico--deleted"
        title={t("userStatusDeleted") || "Deleted"}
        aria-label={t("userStatusDeleted") || "Deleted"}
      >
        <CircleSlash size={15} strokeWidth={2.2} aria-hidden />
      </span>
    );
  }
  if (status === "awaiting_registration") {
    const label = t("userStatusUnregistered") || t("awaitingRegistration") || "Unregistered";
    return (
      <span className="um-status-ico um-status-ico--awaiting" title={label} aria-label={label}>
        <Clock3 size={15} strokeWidth={2.2} aria-hidden />
      </span>
    );
  }
  if (status === "invited") {
    const label = t("userStatusInvited") || "Invited";
    return (
      <span className="um-status-ico um-status-ico--invited" title={label} aria-label={label}>
        <Mail size={15} strokeWidth={2.2} aria-hidden />
      </span>
    );
  }
  if (status === "active") {
    return (
      <span
        className="um-status-ico um-status-ico--active"
        title={t("active") || "Active"}
        aria-label={t("active") || "Active"}
      >
        <CheckCircle2 size={15} strokeWidth={2.2} aria-hidden />
      </span>
    );
  }
  return (
    <span
      className="um-status-ico um-status-ico--inactive"
      title={t("inactive") || "Inactive"}
      aria-label={t("inactive") || "Inactive"}
    >
      <PauseCircle size={15} strokeWidth={2.2} aria-hidden />
    </span>
  );
}

function RoleBadges({ item, t }) {
  // Guest Player is club-scoped: only show the badge for this club's guest membership.
  const roleIdsForClub =
    item?.is_guest === true
      ? item.all_roles
      : Array.isArray(item.all_roles)
        ? item.all_roles.filter((id) => String(id).toLowerCase() !== "r7")
        : item.all_roles;
  const roleNamesForClub =
    item?.is_guest === true
      ? item.all_role_names
      : Array.isArray(item.all_role_names)
        ? item.all_role_names.filter(
            (name) => String(name || "").toLowerCase() !== "guest player"
          )
        : item.all_role_names;
  const roleNames = formatRoleLabels(roleIdsForClub, roleNamesForClub);
  const linkedChildren = Array.isArray(item.linked_children) ? item.linked_children : [];

  if (roleNames.length > 0) {
    return (
      <div className="um-roles">
        <div className="um-roles__row">
          {roleNames.map((roleName, idx) => (
            <span
              key={`${roleName}-${idx}`}
              className="um-chip um-chip--role"
              style={{ backgroundColor: roleBadgeColor(roleName) }}
            >
              {roleName}
            </span>
          ))}
        </div>
        {linkedChildren.length > 0 ? (
          <div className="um-roles__kids">
            <strong>{t("linkedChildren") || "Children"}:</strong>{" "}
            {linkedChildren
              .map(
                (c) =>
                  [c.first_name, c.last_name].filter(Boolean).join(" ") ||
                  c.alias ||
                  c.user_id
              )
              .join(", ")}
          </div>
        ) : null}
      </div>
    );
  }

  const typeMap = {
    1: "Admin",
    2: "Admin",
    3: "Coach",
    4: "Player",
    5: "Parent",
    6: "Committee Member",
  };
  const fallback =
    typeMap[item.type_user_id] ||
    typeMap[item.tbl_type_user?.type_user_id] ||
    "Unknown";
  return (
    <span className="um-chip um-chip--role" style={{ backgroundColor: roleBadgeColor(fallback) }}>
      {fallback}
    </span>
  );
}

function UserActions({
  item,
  accountStatus,
  hasPermission,
  t,
  onInvite,
  onEdit,
  onDelete,
  onDeactivate,
}) {
  const isDeletedUser = accountStatus === "deleted";
  const isUnregistered = accountStatus === "awaiting_registration";
  const isInvited = accountStatus === "invited";

  return (
    <div className="um-actions-cell">
      {!isDeletedUser &&
        isUnregistered &&
        item.player_Id &&
        hasPermission("PLAYER_INVITE") && (
          <Tooltip title={t("inviteToRegister") || "Invite to Register"} arrow>
            <IconButton
              size="small"
              className="um-icon-btn"
              onClick={(e) => {
                e.stopPropagation();
                onInvite(item);
              }}
              sx={{
                color: "#0f9f6e",
                "&:hover": { backgroundColor: "rgba(15, 159, 110, 0.1)" },
              }}
            >
              <EmailIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      {!isDeletedUser && isInvited && hasPermission("PLAYER_INVITE") && (
        <Tooltip title={t("inviteToRegister") || "Invite to Register"} arrow>
          <IconButton
            size="small"
            className="um-icon-btn"
            onClick={(e) => {
              e.stopPropagation();
              onInvite(item);
            }}
            sx={{
              color: "#0f9f6e",
              "&:hover": { backgroundColor: "rgba(15, 159, 110, 0.1)" },
            }}
          >
            <EmailIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!isDeletedUser && hasPermission("UPDATE_USER_INFO") && item.user_id && (
        <Tooltip
          title={
            isUnregistered || isInvited
              ? t("editRosterPlayer") || "Edit roster profile"
              : t("editUser") || "Edit User"
          }
          arrow
        >
          <IconButton
            size="small"
            className="um-icon-btn"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(item);
            }}
            sx={{
              color: "var(--brand-primary)",
              "&:hover": { backgroundColor: "rgba(var(--brand-primary-rgb, 8, 119, 188), 0.1)" },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!isDeletedUser &&
        item.user_id &&
        hasPermission("DELETE_USER") && (
        <Tooltip
          title={
            isUnregistered || isInvited
              ? t("removeRosterStubTitle") ||
                t("removeUserTitle") ||
                "Remove from club roster"
              : t("removeUserTitle") || t("deleteUser") || "Remove member"
          }
          arrow
        >
          <IconButton
            size="small"
            className="um-icon-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item);
            }}
            sx={{
              color: "#dc2626",
              "&:hover": { backgroundColor: "rgba(220, 38, 38, 0.1)" },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!isDeletedUser &&
        !isUnregistered &&
        hasPermission("DEACTIVATE_USER") &&
        item.player_Id && (
          <Tooltip title={t("deactivateUser") || "Deactivate User"} arrow>
            <IconButton
              size="small"
              className="um-icon-btn"
              onClick={(e) => {
                e.stopPropagation();
                onDeactivate(item);
              }}
              sx={{
                color: "#d97706",
                "&:hover": { backgroundColor: "rgba(217, 119, 6, 0.1)" },
              }}
            >
              <FontAwesomeIcon icon={faUserLargeSlash} style={{ fontSize: "14px" }} />
            </IconButton>
          </Tooltip>
        )}
    </div>
  );
}

export function Users() {
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setdata] = useState();
  const [pagination, setpagination] = useState({
    currentPage: 1,
    nextPage: true,
  });
  const [rosterSummary, setRosterSummary] = useState({
    total: 0,
    invited: 0,
    awaiting: 0,
    competition: 0,
    leisure: 0,
    health: 0,
    coaches: 0,
    fltt_form: 0,
  });
  const [id, setid] = useState(false);
  const [showdelete, setshowdelete] = useState(false);
  const [file, setfile] = useState(false);
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(false);
  const [err, seterror] = useState("");
  const [success, setsuccess] = useState("");
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedPlayerToInvite, setSelectedPlayerToInvite] = useState(null);
  const [sortBy, setSortBy] = useState("registration");
  const [updatingFltt, setUpdatingFltt] = useState(false);
  const [newcomerOpen, setNewcomerOpen] = useState(false);
  const [newcomerDossierId, setNewcomerDossierId] = useState(null);
  const [flttUserId, setFlttUserId] = useState(null);
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const clubCountry = storedUser.club?.country || storedUser.club_details?.country || storedUser.country;
  const activeClubId = readStoredActiveClubId(storedUser) || storedUser.club_id;
  const luxembourgClub = isLuxembourgClubCountry(clubCountry) || isLuxembourgFederationClubId(clubCountry, activeClubId);
  const [sortOrder, setSortOrder] = useState("desc");
  const [rosterFilter, setRosterFilter] = useState(null);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [needsClub, setNeedsClub] = useState(false);
  const { t } = useTranslation();

  const info = JSON.parse(localStorage.getItem("user") || "{}");
  const { hasPermission } = usePermissions();
  const canSeeDeletedUsers = isSuperAdminUser(info);
  const canSyncFltt = canUpdateClubFromFltt(
    info,
    info?.club?.country || info?.club_details?.country,
    info?.club_id
  );
  const navigate = useNavigate();
  const matchReview = useFlttMatchReview(info?.club_id, Boolean(info?.club_id), () => {
    getuser(String(pagination?.currentPage || 1));
  });

  useEffect(() => {
    getuser("1");

    const handleClubOrUserChange = () => {
      getuser("1");
    };
    window.addEventListener("smarttt-club-changed", handleClubOrUserChange);
    window.addEventListener("smarttt-user-updated", handleClubOrUserChange);
    return () => {
      window.removeEventListener("smarttt-club-changed", handleClubOrUserChange);
      window.removeEventListener("smarttt-user-updated", handleClubOrUserChange);
    };
  }, [searchQuery, sortBy, sortOrder, rosterFilter]);

  const getuser = async (page) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      const activeClubId =
        readStoredActiveClubId(currentUser) ||
        String(currentUser?.brand_config?.club_id || "").trim();
      const params = {
        search: searchQuery,
        page: page,
        ...(activeClubId ? { club_id: activeClubId } : {}),
        limit: 10,
        sortBy: sortBy || "registration",
        sortOrder: sortOrder || "desc",
        rosterFilter: rosterFilter || null,
      };

      const response = await alluserlist(params);
      if (response.data?.data?.needs_club) {
        setdata([]);
        setNeedsClub(true);
        return;
      }
      setNeedsClub(false);
      const items = Array.isArray(response.data.data.items)
        ? response.data.data.items
        : [];
      setdata(
        canSeeDeletedUsers
          ? items
          : items.filter((item) => resolveAccountStatusClient(item) !== "deleted")
      );
      const payload = response.data.data || {};
      setpagination(payload);
      const summary = payload.rosterSummary;
      if (summary && typeof summary === "object") {
        setRosterSummary({
          total: Number(summary.total) || Number(payload.totalDocs) || 0,
          invited: Number(summary.invited) || 0,
          awaiting: Number(summary.awaiting) || 0,
          competition: Number(summary.competition) || 0,
          leisure: Number(summary.leisure) || 0,
          health: Number(summary.health) || 0,
          coaches: Number(summary.coaches) || 0,
        });
      } else {
        setRosterSummary({
          total: Number(payload.totalDocs) || items.length,
          invited: 0,
          awaiting: 0,
          competition: 0,
          leisure: 0,
          health: 0,
          coaches: 0,
        });
      }
    } catch (error) {
      const message = error?.message || "";
      if (/club_id is required/i.test(message)) {
        setdata([]);
        setNeedsClub(true);
        return;
      }
      console.log(error);
      seterror(message || t("failedToLoadUsers") || "Could not load members");
      setOpen2(true);
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleUpdateFromFltt = async () => {
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    const clubId =
      readStoredActiveClubId(currentUser) ||
      String(currentUser?.brand_config?.club_id || "").trim();
    if (!clubId || updatingFltt) return;
    try {
      setUpdatingFltt(true);
      const response = await updateClubFromFLTT(clubId, true);
      setsuccess(
        response.data?.message ||
          t("updateFromFLTT") ||
          "Updated from FLTT"
      );
      setOpen(true);
      const discovered = response.data?.data?.potential_matches || [];
      if (discovered.length) matchReview.present(discovered);
      await getuser(String(pagination?.currentPage || 1));
    } catch (error) {
      seterror(
        error?.message ||
          t("failedToUpdateClubData") ||
          "Could not update from FLTT"
      );
      setOpen2(true);
    } finally {
      setUpdatingFltt(false);
    }
  };

  const handledelete = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      const params = {
        user_id: id,
        club_id:
          readStoredActiveClubId(currentUser) ||
          String(currentUser?.brand_config?.club_id || info?.club_id || "").trim() ||
          undefined,
      };
      const response = await userdelete(params);
      setdata((prev) =>
        Array.isArray(prev) ? prev.filter((item) => item.user_id !== id) : prev
      );
      setsuccess(
        response?.data?.message || t("removeUserConfirm") || "Member removed"
      );
      setOpen(true);
      setshowdelete(false);
      await getuser(pagination.currentPage || "1");
    } catch (error) {
      seterror(error.message);
      setOpen2(true);
      if (error.code === 401) {
        localStorage.clear();
        navigate("/");
      }
      setshowdelete(false);
    }
  };

  const handlefile = async () => {
    try {
      const response = await generatefile({});
      const link = document.createElement("a");
      link.href = response.data.data;
      link.click();
    } catch (error) {
      seterror(error.message);
      setOpen(true);
      if (error.code === 401) {
        localStorage.clear();
        navigate("/");
      }
    }
  };

  const handleupload = async (upload) => {
    try {
      const params = new FormData();
      params.append("userFile", upload);
      const response = await uploadExcelOfUser(params);
      setsuccess(response.data.message);
      setOpen(true);
    } catch (error) {
      seterror(error.error);
      setOpen2(true);
      if (error.code === 401) {
        localStorage.clear();
        navigate("/");
      }
    }
  };

  const onClose = () => setshowdelete(false);
  const handlePageChange = (pageNumber) => getuser(pageNumber);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      const newestFirst =
        column === "ranking" ||
        column === "registration" ||
        column === "lastLogin";
      setSortOrder(newestFirst ? "desc" : "asc");
    }
  };

  const handleRosterFilter = (filterKey) => {
    if (filterKey == null) {
      setRosterFilter(null);
    } else {
      setRosterFilter((prev) => (prev === filterKey ? null : filterKey));
    }
    setExpandedCardId(null);
  };

  const sortedData = data || [];

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <UnfoldMoreIcon sx={{ fontSize: 16, color: "var(--text-secondary)" }} />;
    }
    return sortOrder === "asc" ? (
      <ArrowUpwardIcon sx={{ fontSize: 16, color: "var(--brand-primary)" }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 16, color: "var(--brand-primary)" }} />
    );
  };

  const handleinactive = async (playerId) => {
    try {
      const response = await playeractive({ player_id: playerId });
      setsuccess(response.data.message);
      setOpen(true);
      getuser("1");
    } catch (error) {
      seterror(error.message);
      setOpen2(true);
    }
  };

  const inviteUser = (item) => {
    setSelectedPlayerToInvite(item);
    setInviteDialogOpen(true);
  };

  const editUser = (item) => {
    navigate("/EditUser", { state: { info: item } });
  };

  const deleteUser = (item) => {
    setid(item.user_id);
    setshowdelete(true);
  };

  const renderIdentity = (item, accountStatus) => {
    const tone =
      accountStatus === "deleted"
        ? "deleted"
        : accountStatus === "awaiting_registration" || accountStatus === "invited"
          ? accountStatus === "invited"
            ? "invited"
            : "awaiting"
          : "active";
    return (
      <div className="um-identity">
        <div className={`um-avatar um-avatar--${tone}`}>
          {getInitials(item)}
          {item.fltt_classement ? (
            <span className={`um-avatar__rank um-rank--${classementTone(item.fltt_classement)}`}>
              {item.fltt_classement}
            </span>
          ) : null}
        </div>
        <div className="um-identity__text">
          <p className="um-identity__name">{displayNameOf(item)}</p>
          <GuestVisitBadge item={item} t={t} />
          {item.newcomer ? (
            <button
              type="button"
              className="um-chip um-chip--newcomer"
              onClick={() => setNewcomerDossierId(item.newcomer.dossier_id)}
            >
              Newcomer · {item.newcomer.trial_days_used}/{item.newcomer.trial_day_limit}
              {item.newcomer.missing_count ? ` · ${item.newcomer.missing_count} papers` : ""}
            </button>
          ) : null}
          {luxembourgClub && item.fltt_form && !item.fltt_form.ready ? (
            <button
              type="button"
              className="um-chip um-chip--form"
              onClick={(event) => {
                event.stopPropagation();
                setFlttUserId(item.user_id);
              }}
            >
              FLTT form · {item.fltt_form.missing_count} to fill
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const actionProps = {
    hasPermission,
    t,
    onInvite: inviteUser,
    onEdit: editUser,
    onDelete: deleteUser,
    onDeactivate: (item) => handleinactive(item.player_Id),
  };

  return (
    <div className="um-board">
      <div className="um-board__glow" aria-hidden="true" />

      <header className="um-hero">
        <div className="um-hero__copy">
          <p className="um-hero__kicker">
            {t("userManagementKicker", { defaultValue: "Club directory" })}
          </p>
          <h1 className="um-hero__title">{t("alluser") || "Members"}</h1>
          <p className="um-hero__sub">
            {t("userManagementHint") ||
              "Search the roster, manage members who never use the app, invite players to register, and update presence from one place."}
          </p>
        </div>
        <div className="um-hero__stats" aria-label={t("alluser") || "Members"}>
          <button
            type="button"
            className={`um-stat${rosterFilter == null ? " is-active" : ""}`}
            aria-pressed={rosterFilter == null}
            onClick={() => handleRosterFilter(null)}
          >
            <span className="um-stat__value">{rosterSummary.total}</span>
            <span className="um-stat__label">
              {t("umStatTotalMembers", { defaultValue: "Members" })}
            </span>
          </button>
          <button
            type="button"
            className={`um-stat um-stat--coaches${
              rosterFilter === "coaches" ? " is-active" : ""
            }`}
            aria-pressed={rosterFilter === "coaches"}
            onClick={() => handleRosterFilter("coaches")}
          >
            <span className="um-stat__value">{rosterSummary.coaches}</span>
            <span className="um-stat__label">
              {t("umStatCoaches", { defaultValue: "Coaches" })}
            </span>
          </button>
          <button
            type="button"
            className={`um-stat um-stat--competition${
              rosterFilter === "competition" ? " is-active" : ""
            }`}
            aria-pressed={rosterFilter === "competition"}
            onClick={() => handleRosterFilter("competition")}
          >
            <span className="um-stat__value">{rosterSummary.competition}</span>
            <span className="um-stat__label">
              {t("umStatCompetitionPlayers", { defaultValue: "Competition players" })}
            </span>
          </button>
          <button
            type="button"
            className={`um-stat um-stat--leisure${
              rosterFilter === "leisure" ? " is-active" : ""
            }`}
            aria-pressed={rosterFilter === "leisure"}
            onClick={() => handleRosterFilter("leisure")}
          >
            <span className="um-stat__value">{rosterSummary.leisure}</span>
            <span className="um-stat__label">
              {t("umStatLeisurePlayers", { defaultValue: "Leisure players" })}
            </span>
          </button>
          <button
            type="button"
            className={`um-stat um-stat--health${
              rosterFilter === "health" ? " is-active" : ""
            }`}
            aria-pressed={rosterFilter === "health"}
            onClick={() => handleRosterFilter("health")}
          >
            <span className="um-stat__value">{rosterSummary.health}</span>
            <span className="um-stat__label">
              {t("umStatHealthPlayers", { defaultValue: "Sport health players" })}
            </span>
          </button>
          <button
            type="button"
            className={`um-stat um-stat--invited${
              rosterFilter === "invited" ? " is-active" : ""
            }`}
            aria-pressed={rosterFilter === "invited"}
            onClick={() => handleRosterFilter("invited")}
          >
            <span className="um-stat__value">{rosterSummary.invited}</span>
            <span className="um-stat__label">
              {t("umStatInvitedMembers", { defaultValue: "Invited members" })}
            </span>
          </button>
          {luxembourgClub ? (
            <button
              type="button"
              className={`um-stat um-stat--form${rosterFilter === "fltt_form" ? " is-active" : ""}`}
              aria-pressed={rosterFilter === "fltt_form"}
              onClick={() => handleRosterFilter("fltt_form")}
            >
              <span className="um-stat__value">{rosterSummary.fltt_form || 0}</span>
              <span className="um-stat__label">FLTT form to fill</span>
            </button>
          ) : null}
        </div>
      </header>

      {matchReview.proposals.length > 0 && (
        <div className="fltt-match-banner">
          <p className="fltt-match-banner__text">
            {t("flttMatchBanner", {
              count: matchReview.proposals.length,
              defaultValue: "Potential match to review",
            })}
          </p>
          <button
            type="button"
            className="fltt-match-banner__button"
            onClick={() => matchReview.setOpen(true)}
          >
            {t("flttMatchReview", { defaultValue: "Review" })}
          </button>
        </div>
      )}

      <div className="um-toolbar">
        <div className="um-search">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="um-search__icon" />
          <input
            className="um-search__input"
            type="search"
            placeholder={t("search") || "Search members"}
            value={searchQuery}
            onChange={handleSearch}
            aria-label={t("search") || "Search"}
          />
        </div>
        <div className="um-actions">
          {canSyncFltt && (
            <button
              type="button"
              className="um-btn um-btn--primary"
              onClick={handleUpdateFromFltt}
              disabled={updatingFltt}
            >
              {updatingFltt
                ? t("updating") || "Updating..."
                : t("updateFromFLTT") || "Update from FLTT"}
            </button>
          )}
          {hasPermission("USER_CREATE") && (
            <NavLink to="/UsersAdd" className="um-btn um-btn--primary">
              {t("addnew")}
            </NavLink>
          )}
          <button type="button" className="um-btn um-btn--primary" onClick={() => setNewcomerOpen(true)}>
            Add newcomer
          </button>
          {hasPermission("DOWNLOAD_XLSX_USER_FILE") && (
            <button type="button" className="um-btn um-btn--ghost" onClick={handlefile}>
              {t("download")}
            </button>
          )}
          {hasPermission("CREATE_USER_XLSX") && (
            <button type="button" className="um-btn um-btn--ghost" onClick={() => setfile(true)}>
              {t("upload")}
            </button>
          )}
        </div>
        <div className="um-sort" role="toolbar" aria-label={t("sort") || "Sort"}>
          {[
            {
              id: "registration",
              label: t("registrationDate", { defaultValue: "Registration" }),
            },
            {
              id: "lastLogin",
              label: t("lastLogin", { defaultValue: "Last login" }),
            },
            { id: "name", label: t("name") || "Name" },
            { id: "classement", label: "Classement" },
            { id: "ranking", label: "Ranking (TS)" },
          ].map((col) => (
            <button
              key={col.id}
              type="button"
              className={`um-sort__chip ${sortBy === col.id ? "is-active" : ""}`}
              onClick={() => handleSort(col.id)}
            >
              {col.label}
              {getSortIcon(col.id)}
            </button>
          ))}
        </div>
      </div>

      {sortedData?.length > 0 ? (
        <>
          <div className="um-table-wrap">
            <table className="um-table">
              <thead>
                <tr>
                  <th className="um-th--sort um-col--name" onClick={() => handleSort("name")}>
                    <span className="um-th__inner">
                      {t("name")}
                      {getSortIcon("name")}
                    </span>
                  </th>
                  <th className="um-th--sort" onClick={() => handleSort("classement")}>
                    <span className="um-th__inner">
                      Classement
                      {getSortIcon("classement")}
                    </span>
                  </th>
                  <th className="um-th--sort" onClick={() => handleSort("ranking")}>
                    <span className="um-th__inner">
                      Ranking (TS)
                      {getSortIcon("ranking")}
                    </span>
                  </th>
                  <th className="um-col--email">{t("mail")}</th>
                  <th className="um-th--sort" onClick={() => handleSort("registration")}>
                    <span className="um-th__inner">
                      {t("registrationDate", { defaultValue: "Registration" })}
                      {getSortIcon("registration")}
                    </span>
                  </th>
                  <th className="um-th--sort" onClick={() => handleSort("lastLogin")}>
                    <span className="um-th__inner">
                      {t("lastLogin", { defaultValue: "Last login" })}
                      {getSortIcon("lastLogin")}
                    </span>
                  </th>
                  <th>{t("invitationSentAt") || "Invited"}</th>
                  <th>{t("usertype")}</th>
                  <th>{t("active")}</th>
                  <th>{t("action")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item, index) => {
                  const accountStatus = resolveAccountStatusClient(item);
                  const inviteRaw =
                    item.invitation_sent_at ||
                    (accountStatus === "invited" ||
                    accountStatus === "awaiting_registration"
                      ? item.createdAt
                      : null);
                  const inviteLabel = formatInviteDate(inviteRaw);
                  const registrationLabel = formatInviteDate(item.createdAt);
                  const lastLoginLabel = formatInviteDate(item.user_last_session);
                  return (
                    <tr
                      key={item.user_id || item.player_Id || `user-${index}`}
                      className={accountStatus === "deleted" ? "is-deleted" : undefined}
                    >
                      <td className="um-col--name">
                        <div className="um-identity-wrap">
                          {renderIdentity(item, accountStatus)}
                          {(accountStatus === "awaiting_registration" ||
                            accountStatus === "invited" ||
                            accountStatus === "deleted") && (
                            <div className="um-identity__meta">
                              <StatusChip
                                status={accountStatus}
                                isActive={item.isActive}
                                t={t}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        {item.fltt_classement ? (
                          <span
                            className={`um-rank um-rank--${classementTone(item.fltt_classement)}`}
                          >
                            {item.fltt_classement}
                          </span>
                        ) : (
                          <span className="um-muted">—</span>
                        )}
                      </td>
                      <td>
                        {item.fltt_ranking_points ? (
                          <span className="um-points-wrap">
                            <span className="um-points">
                              {parseFloat(item.fltt_ranking_points).toFixed(2)}
                            </span>
                            {formatRankingPlace(item.fltt_ranking_place) ? (
                              <span className="um-place">
                                {formatRankingPlace(item.fltt_ranking_place)}
                              </span>
                            ) : null}
                            {formatPointsWeekDelta(item.fltt_points_week_delta) ? (
                              <span
                                className={`um-week-chip um-week-chip--${
                                  formatPointsWeekDelta(item.fltt_points_week_delta).tone
                                }`}
                              >
                                {formatPointsWeekDelta(item.fltt_points_week_delta).text}
                              </span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="um-muted">—</span>
                        )}
                      </td>
                      <td className="um-col--email">
                        <DesktopEmailCell
                          email={item.user_email || item.contact_email || ""}
                          viaParent={Boolean(item.email_is_parent_contact)}
                          t={t}
                          onCopied={() => {
                            setsuccess(t("emailCopied") || "Email copied");
                            setOpen(true);
                          }}
                        />
                      </td>
                      <td>
                        {registrationLabel ? (
                          <span className="um-date">{registrationLabel}</span>
                        ) : (
                          <span className="um-muted">—</span>
                        )}
                      </td>
                      <td>
                        {lastLoginLabel ? (
                          <span className="um-date">{lastLoginLabel}</span>
                        ) : (
                          <span className="um-muted">
                            {t("never", { defaultValue: "Never" })}
                          </span>
                        )}
                      </td>
                      <td>
                        {inviteLabel ? (
                          <span className="um-date">{inviteLabel}</span>
                        ) : (
                          <span className="um-muted">—</span>
                        )}
                      </td>
                      <td>
                        <RoleBadges item={item} t={t} />
                      </td>
                      <td>
                        <StatusChip
                          status={accountStatus}
                          isActive={item.isActive}
                          t={t}
                        />
                      </td>
                      <td>
                        <UserActions
                          item={item}
                          accountStatus={accountStatus}
                          {...actionProps}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="um-cards">
            {sortedData.map((item, index) => {
              const accountStatus = resolveAccountStatusClient(item);
              const inviteRaw =
                item.invitation_sent_at ||
                (accountStatus === "invited" ||
                accountStatus === "awaiting_registration"
                  ? item.createdAt
                  : null);
              const inviteShort = formatInviteDateShort(inviteRaw);
              const registrationShort = formatInviteDateShort(item.createdAt);
              const lastLoginShort = formatInviteDateShort(item.user_last_session);
              const cardId = String(item.user_id || item.player_Id || `card-${index}`);
              const expanded = expandedCardId === cardId;
              const cardState =
                accountStatus === "deleted"
                  ? "deleted"
                  : accountStatus === "awaiting_registration"
                    ? "awaiting"
                    : accountStatus === "invited"
                      ? "invited"
                      : accountStatus === "active"
                        ? "active"
                        : "inactive";
              const email = item.user_email || item.contact_email || "";
              const ranking = item.fltt_ranking_points
                ? parseFloat(item.fltt_ranking_points).toFixed(1)
                : null;
              const classement = item.fltt_classement || null;

              return (
                <article
                  key={cardId}
                  className={`um-card is-${cardState}${expanded ? " is-expanded" : ""}`}
                >
                  <button
                    type="button"
                    className="um-card__hit"
                    aria-expanded={expanded}
                    onClick={() =>
                      setExpandedCardId((prev) => (prev === cardId ? null : cardId))
                    }
                  >
                    <div
                      className={`um-avatar um-avatar--${
                        accountStatus === "deleted"
                          ? "deleted"
                          : accountStatus === "invited"
                            ? "invited"
                            : accountStatus === "awaiting_registration"
                              ? "awaiting"
                              : "active"
                      }`}
                    >
                      {getInitials(item)}
                    </div>
                    <div className="um-card__main">
                      <div className="um-card__title">
                        <p className="um-card__name">{displayNameOf(item)}</p>
                        <StatusIcon
                          status={accountStatus}
                          isActive={item.isActive}
                          t={t}
                        />
                      </div>
                      <GuestVisitBadge item={item} t={t} />
                      {item.newcomer ? (
                        <button
                          type="button"
                          className="um-chip um-chip--newcomer"
                          onClick={() => setNewcomerDossierId(item.newcomer.dossier_id)}
                        >
                          Newcomer · {item.newcomer.trial_days_used}/{item.newcomer.trial_day_limit}
                        </button>
                      ) : null}
                      {luxembourgClub && item.fltt_form && !item.fltt_form.ready ? (
                        <button
                          type="button"
                          className="um-chip um-chip--form"
                          onClick={(event) => {
                            event.stopPropagation();
                            setFlttUserId(item.user_id);
                          }}
                        >
                          FLTT form · {item.fltt_form.missing_count} to fill
                        </button>
                      ) : null}
                      <div className="um-card__facts" aria-label={t("action") || "Details"}>
                        <span
                          className="um-fact"
                          title="Classement"
                        >
                          <Medal size={13} strokeWidth={2.2} aria-hidden />
                          <em>
                            {classement ? (
                              <span
                                className={`um-rank um-rank--${classementTone(classement)}`}
                              >
                                {classement}
                              </span>
                            ) : (
                              "—"
                            )}
                          </em>
                        </span>
                        <span
                          className="um-fact"
                          title="Ranking (TS)"
                        >
                          <Gauge size={13} strokeWidth={2.2} aria-hidden />
                          <em>
                            {ranking || "—"}
                            {formatRankingPlace(item.fltt_ranking_place)
                              ? ` ${formatRankingPlace(item.fltt_ranking_place)}`
                              : ""}
                            {formatPointsWeekDelta(item.fltt_points_week_delta)
                              ? ` ${formatPointsWeekDelta(item.fltt_points_week_delta).text}`
                              : ""}
                          </em>
                        </span>
                        {registrationShort ? (
                          <span
                            className="um-fact"
                            title={t("registrationDate", { defaultValue: "Registration" })}
                          >
                            <CalendarDays size={13} strokeWidth={2.2} aria-hidden />
                            <em>{registrationShort}</em>
                          </span>
                        ) : null}
                        <span
                          className="um-fact"
                          title={t("lastLogin", { defaultValue: "Last login" })}
                        >
                          <Clock3 size={13} strokeWidth={2.2} aria-hidden />
                          <em>
                            {lastLoginShort ||
                              t("never", { defaultValue: "Never" })}
                          </em>
                        </span>
                        {inviteShort && accountStatus !== "active" ? (
                          <span
                            className="um-fact"
                            title={t("invitationSentAt") || "Invited"}
                          >
                            <Mail size={13} strokeWidth={2.2} aria-hidden />
                            <em>{inviteShort}</em>
                          </span>
                        ) : null}
                      </div>
                      <div className="um-card__roles">
                        <RoleBadges item={item} t={t} />
                      </div>
                    </div>
                    <ChevronDown
                      className={`um-card__chev${expanded ? " is-open" : ""}`}
                      size={18}
                      strokeWidth={2.2}
                      aria-hidden
                    />
                  </button>
                  {expanded ? (
                    <div className="um-card__drawer">
                      <div className="um-card__email">
                        <Mail size={14} strokeWidth={2.2} aria-hidden />
                        <div className="um-card__email-text">
                          {email ? (
                            <>
                              <a
                                className="um-card__email-link"
                                href={`mailto:${email}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {email}
                              </a>
                              {item.email_is_parent_contact ? (
                                <span className="um-email__via">
                                  {t("viaParent") || "via parent"}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <span className="um-muted">{t("notSet") || "Not set"}</span>
                          )}
                        </div>
                      </div>
                      <UserActions
                        item={item}
                        accountStatus={accountStatus}
                        {...actionProps}
                      />
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>

          <div className="um-pager">
            <Pagination pagedata={pagination} onPageChange={handlePageChange} />
          </div>
        </>
      ) : (
        <div className="um-empty">
          <p className="um-empty__title">
            {needsClub
              ? t("selectClubForMembers", { defaultValue: "Choose a club first" })
              : rosterFilter
              ? t("noUsersInFilter", { defaultValue: "No members in this group" })
              : t("noUsersYet") || "No members yet"}
          </p>
          <p className="um-empty__sub">
            {needsClub
              ? t("selectClubForMembersHint", {
                  defaultValue:
                    "User management is per club. Switch to the club whose members you want to edit.",
                })
              : rosterFilter
              ? t("noUsersInFilterHint", {
                  defaultValue: "Tap the card again or choose Members to clear the filter.",
                })
              : t("noUsersYetHint") ||
                "Add a member or upload a roster file to get started."}
          </p>
          {needsClub ? (
            <button
              type="button"
              className="um-btn um-btn--primary um-empty__action"
              onClick={() => openSwitchClubDialog()}
            >
              {t("switchClub", { defaultValue: "Switch club" })}
            </button>
          ) : null}
        </div>
      )}

      <Uploadfile isOpen={file} setisOpen={setfile} handleupload={handleupload} />
      <Deletepopup
        isOpen={showdelete}
        setisOpen={setshowdelete}
        handledelete={handledelete}
        onClose={onClose}
        title={t("removeUserTitle")}
        description={
          t("removeUserHistoryKeptMultiClub") ||
          t("removeUserHistoryKept") ||
          "Removed from this club only. If they belong to other clubs, those memberships and roles stay. Training history is kept."
        }
        confirmLabel={t("removeUserConfirm")}
      />
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert variant="filled">{success}</Alert>
      </Snackbar>
      <Snackbar
        open={open2}
        autoHideDuration={3000}
        onClose={() => setOpen2(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert variant="filled" severity="error">
          {err}
        </Alert>
      </Snackbar>

      <FlttFormDialog
        open={Boolean(flttUserId)}
        userId={flttUserId}
        clubId={activeClubId}
        onClose={() => setFlttUserId(null)}
        onSaved={() => getuser(pagination.currentPage)}
      />
      <AddNewcomerDialog
        open={newcomerOpen}
        onClose={() => setNewcomerOpen(false)}
        onCreated={() => getuser(String(pagination?.currentPage || 1))}
      />
      <NewcomerDossierDialog
        open={Boolean(newcomerDossierId)}
        dossierId={newcomerDossierId}
        onClose={() => {
          setNewcomerDossierId(null);
          getuser(String(pagination?.currentPage || 1));
        }}
      />
      <FlttMatchProposalDialog
        open={matchReview.open}
        proposals={matchReview.proposals}
        busy={matchReview.busy}
        error={matchReview.error}
        onAccept={matchReview.accept}
        onReject={matchReview.reject}
        onLater={matchReview.later}
      />
      <InvitePlayerDialog
        open={inviteDialogOpen}
        onClose={() => {
          setInviteDialogOpen(false);
          setSelectedPlayerToInvite(null);
        }}
        player={selectedPlayerToInvite}
        onInviteSent={(_userId, email) => {
          getuser(pagination.currentPage);
          setsuccess(`Invitation sent successfully to ${email}`);
          setOpen(true);
        }}
      />
    </div>
  );
}
